import {
  ArrowRight,
  Building2,
  Bus,
  Database,
  FlaskConical,
  GraduationCap,
  HeartPulse,
  Landmark,
  Leaf,
  type LucideIcon,
  Scale,
  Sprout,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { loadPublicOrganisations, type OrganisationNode } from '../lib/organisations.js';
import type { PortalDataset, PortalOrganisation } from '../lib/portal.js';
import { useTenant, type TenantGebiet } from '../lib/tenant.js';
import { cn } from '../lib/utils.js';
import { HeroAnimation } from './HeroAnimation.js';

/**
 * Rahmen der oeffentlichen Seiten (Startseite, Datensaetze, Datensatz): Kopf mit Wappen, Navigation und
 * „Anmelden“, Fuss mit Pflichtangaben der Kommune. Auf der Startseite liegt der Kopf transparent ueber
 * dem Titelbild (`overlay`), sonst in der Hausfarbe.
 */
export const THEME_ICONS: Record<string, LucideIcon> = {
  AGRI: Sprout,
  ECON: TrendingUp,
  EDUC: GraduationCap,
  ENER: Zap,
  ENVI: Leaf,
  GOVE: Landmark,
  HEAL: HeartPulse,
  JUST: Scale,
  REGI: Building2,
  SOCI: Users,
  TECH: FlaskConical,
  TRAN: Bus,
  OTHER: Database,
};

export const ANMELDEN = '/uebersicht';

/** Offene Datensaetze (einmal je Seite geladen); null solange sie laden. */
export function usePortalDatasets(): PortalDataset[] | null {
  const [items, setItems] = useState<PortalDataset[] | null>(null);
  useEffect(() => {
    fetch('/oeffentlich/datasets', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b: { items: PortalDataset[] }) => setItems(b.items))
      .catch(() => setItems([]));
  }, []);
  return items;
}

/** Gliederung (oeffentliche Angaben und Zahlen); null solange sie laedt, leer bei Fehler. */
export function usePublicGliederung(): OrganisationNode[] | null {
  const [items, setItems] = useState<OrganisationNode[] | null>(null);
  useEffect(() => {
    loadPublicOrganisations()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);
  return items;
}

/** Gibt es den oeffentlichen Zwilling (Basisdaten im Katalog)? */
export function useTwinAvailable(): boolean {
  const [twin, setTwin] = useState(false);
  useEffect(() => {
    fetch('/twin-public/basedata', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((b: { items?: unknown[] }) => setTwin((b.items ?? []).length > 0))
      .catch(() => setTwin(false));
  }, []);
  return twin;
}

export function usePageTitle(title: string): void {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/**
 * Hintergrund des Titelbereichs: Titelbild (Verwaltung › Gliederung) oder Hausfarbe, abgedunkelt, damit
 * Schrift auf jedem Foto lesbar bleibt (Kontrast >= 4.5:1). Mit eingeschalteter Animation (Verwaltung ›
 * Erscheinungsbild) liegt das Foto nur noch blass darunter und das Gebiet bewegt sich darueber.
 */
export function HeroBackdrop({ src, gebiete, pauseClassName }: { src?: string | null; gebiete: TenantGebiet[]; pauseClassName?: string }): JSX.Element {
  const tenant = useTenant();
  const photo = src === undefined ? tenant.heroUrl : src;
  const animated = tenant.heroAnimation;
  return (
    <>
      {animated ? <div aria-hidden className="absolute inset-0 -z-10 bg-brand-900" /> : null}
      {photo ? (
        <img src={photo} alt="" className={cn('absolute inset-0 -z-10 h-full w-full object-cover', animated && 'opacity-25 mix-blend-luminosity')} />
      ) : (
        <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-900 via-brand-900 to-brand-700" />
      )}
      <div aria-hidden className={cn('absolute inset-0 -z-10 bg-gradient-to-r', animated ? 'from-black/60 via-black/25 to-transparent' : 'from-black/75 via-black/45 to-black/10')} />
      <div aria-hidden className="absolute inset-x-0 bottom-0 -z-10 h-2/3 bg-gradient-to-t from-black/60 to-transparent" />
      {animated ? <HeroAnimation gebiete={gebiete} hue={tenant.brandHue} chroma={tenant.brandChroma} pauseClassName={pauseClassName} /> : null}
    </>
  );
}

const NAV_LINK = 'py-2 text-white/90 no-underline hover:text-white hover:underline underline-offset-4';

export function PortalHeader({ twin, current }: { twin: boolean; current?: 'daten' }): JSX.Element {
  const tenant = useTenant();
  return (
    <header className="border-t-4 border-t-brand-500 text-white">
      <div className="mx-auto flex max-w-6xl items-center gap-x-8 gap-y-3 px-4 py-4 md:flex-wrap md:px-6">
        <a href="/" className="flex min-w-0 items-center gap-3 text-white no-underline" aria-label={tenant.logoUrl || tenant.logoHellUrl ? `${tenant.name} – Startseite` : undefined}>
          {tenant.logoHellUrl ? (
            <img src={tenant.logoHellUrl} alt="" className="h-14 w-auto max-w-[15rem] object-contain object-left drop-shadow md:h-16" />
          ) : tenant.logoUrl ? (
            // Nur das Logo fuer helle Flaechen: auf weissem Feld, damit es ueber dem Titelbild lesbar bleibt.
            <span className="flex h-14 items-center rounded-md bg-white px-3 py-1.5 shadow-sm md:h-16">
              <img src={tenant.logoUrl} alt="" className="h-full w-auto max-w-[13rem] object-contain" />
            </span>
          ) : (
            <>
              {tenant.wappenUrl ? (
                <img src={tenant.wappenUrl} alt="" className="h-12 w-auto max-w-12 object-contain drop-shadow" />
              ) : (
                <span aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-white/15 font-display text-xl font-bold ring-1 ring-white/30 backdrop-blur-sm">
                  {tenant.shortName.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-display text-lg font-bold">{tenant.name}</span>
                <span className="text-sm text-white/75">Offene Daten</span>
              </span>
            </>
          )}
        </a>
        <nav aria-label="Hauptnavigation" className="hidden flex-1 flex-wrap gap-x-6 gap-y-1 font-medium md:flex">
          <a href="/daten" className={NAV_LINK} aria-current={current === 'daten' ? 'page' : undefined}>
            Datensätze
          </a>
          <a href="/#themen" className={NAV_LINK}>Themen</a>
          {twin ? <a href="/public/twin" className={NAV_LINK}>Karte &amp; 3D</a> : null}
          <a href="/#nutzen" className={NAV_LINK}>Daten nutzen</a>
        </nav>
        <a
          href={ANMELDEN}
          className="ml-auto inline-flex min-h-11 shrink-0 items-center gap-2 rounded-md bg-white px-4 font-semibold text-brand-900 no-underline shadow-sm hover:bg-white/90 md:px-5"
        >
          Anmelden
          <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
      </div>
    </header>
  );
}

/**
 * Fuss mit den Pflichtangaben. Mit `org` (Seite einer Gemeinde, ihr Datensatz) stehen Name, Webseite,
 * Impressum und Datenschutz dieses Eintrags dort; fehlt etwas, gilt schon die Angabe darueber.
 */
export function PortalFooter({ org }: { org?: (PortalOrganisation & { homepage?: string | null }) | null }): JSX.Element {
  const t = useTenant();
  const tenant = org
    ? { ...t, name: org.name, publisherHomepage: org.homepage ?? t.publisherHomepage, imprintUrl: org.imprint_url, privacyUrl: org.privacy_url }
    : t;
  const link = 'text-foreground/80 underline-offset-4 hover:text-primary hover:underline';
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 text-sm md:grid-cols-[1.4fr_1fr_1fr] md:px-6">
        <div className="space-y-2">
          <p className="font-display text-base font-bold">{tenant.name}</p>
          <p className="max-w-sm text-muted-foreground">Offene Daten der Kommune – frei nutzbar unter der Lizenz, die an jedem Datensatz steht.</p>
        </div>
        <nav aria-label="Portal" className="flex flex-col gap-2">
          <p className="font-semibold">Portal</p>
          <a href="/daten" className={link}>Alle Datensätze</a>
          <a href="/#nutzen" className={link}>Schnittstellen</a>
          <a href={ANMELDEN} className={link}>Anmelden für Mitarbeitende</a>
        </nav>
        <nav aria-label="Kommune" className="flex flex-col gap-2">
          <p className="font-semibold">Kommune</p>
          {tenant.publisherHomepage ? <a href={tenant.publisherHomepage} className={link}>Webseite der Kommune</a> : null}
          {tenant.publisherEmail ? <a href={`mailto:${tenant.publisherEmail}`} className={link}>Kontakt</a> : null}
          {tenant.imprintUrl ? <a href={tenant.imprintUrl} className={link}>Impressum</a> : null}
          {tenant.privacyUrl ? <a href={tenant.privacyUrl} className={link}>Datenschutz</a> : null}
        </nav>
      </div>
      <div className="border-t border-border">
        <p className="mx-auto max-w-6xl px-4 py-4 text-xs text-muted-foreground md:px-6">Betrieben mit CiviPort</p>
      </div>
    </footer>
  );
}

export function SkipLink(): JSX.Element {
  return (
    <a
      href="#inhalt"
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:bg-[#ffdd00] focus:px-3 focus:py-2 focus:text-[#0b0c0c]"
    >
      Zum Inhalt springen
    </a>
  );
}

/** Unterseiten: Kopf in der Hausfarbe mit Seitentitel darunter. */
export function PortalPage({
  twin,
  current,
  children,
  intro,
  org,
}: {
  twin: boolean;
  current?: 'daten';
  intro: ReactNode;
  children: ReactNode;
  org?: PortalOrganisation | null;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-secondary font-sans text-foreground">
      <SkipLink />
      <div className="bg-brand-900 text-white">
        <PortalHeader twin={twin} current={current} />
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-6 md:px-6 md:pb-12">{intro}</div>
      </div>
      <main id="inhalt" className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 md:px-6">
        {children}
      </main>
      <PortalFooter org={org} />
    </div>
  );
}
